import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["input", "results", "empty", "hint"]
  static values = { url: String }

  connect() {
    this.items = []
    this.activeIndex = -1
    this.lastRequestId = 0
    this.onGlobalKeydown = this.onGlobalKeydown.bind(this)
    document.addEventListener("keydown", this.onGlobalKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onGlobalKeydown)
  }

  onGlobalKeydown(event) {
    const isToggleCombo =
      (event.metaKey || event.ctrlKey) &&
      !event.shiftKey &&
      !event.altKey &&
      event.key.toLowerCase() === "j"

    if (isToggleCombo) {
      event.preventDefault()
      this.toggle()
      return
    }

    if (event.key === "Escape" && this.isOpen()) {
      event.preventDefault()
      this.close()
    }
  }

  isOpen() {
    return !this.element.hidden
  }

  toggle() {
    this.isOpen() ? this.close() : this.open()
  }

  open() {
    this.element.hidden = false
    this.element.classList.add("is-open")
    requestAnimationFrame(() => {
      this.inputTarget.focus()
      this.inputTarget.select()
    })
    if (this.inputTarget.value.trim().length > 0) this.search()
  }

  close() {
    this.element.classList.remove("is-open")
    this.element.hidden = true
    this.activeIndex = -1
  }

  backdrop(event) {
    if (event.target === this.element) this.close()
  }

  input() {
    clearTimeout(this.debounce)
    this.debounce = setTimeout(() => this.search(), 80)
  }

  async search() {
    const term = this.inputTarget.value.trim()
    const requestId = ++this.lastRequestId

    if (term.length === 0) {
      this.renderResults([])
      return
    }

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("search", term)

    try {
      const response = await fetch(url.toString(), {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      })
      if (!response.ok) return
      if (requestId !== this.lastRequestId) return
      if (this.inputTarget.value.trim() !== term) return
      const data = await response.json()
      this.renderResults(data)
    } catch (_) {
      // swallow — network hiccups shouldn't break the overlay
    }
  }

  renderResults(items) {
    this.items = items
    this.resultsTarget.innerHTML = ""

    const hasQuery = this.inputTarget.value.trim().length > 0

    if (items.length === 0) {
      this.emptyTarget.hidden = !hasQuery
      this.hintTarget.hidden = hasQuery
      return
    }

    this.emptyTarget.hidden = true
    this.hintTarget.hidden = true

    items.forEach((item, i) => {
      const li = document.createElement("li")
      li.className = "spotlight-item"
      li.setAttribute("role", "option")
      li.dataset.index = i

      const name = document.createElement("span")
      name.className = "spotlight-item-name"
      name.textContent = item.name
      li.appendChild(name)

      if (item.market) {
        const meta = document.createElement("span")
        meta.className = "spotlight-item-meta"
        meta.textContent = item.market
        li.appendChild(meta)
      }

      li.addEventListener("mousemove", () => this.setActive(i))
      li.addEventListener("click", () => this.navigateTo(item.path))
      this.resultsTarget.appendChild(li)
    })

    this.setActive(0)
  }

  setActive(index) {
    this.activeIndex = index
    const nodes = this.resultsTarget.querySelectorAll(".spotlight-item")
    nodes.forEach((el, i) => el.classList.toggle("is-active", i === index))
    const active = nodes[index]
    if (active) active.scrollIntoView({ block: "nearest" })
  }

  navigate(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      if (this.items.length === 0) return
      this.setActive((this.activeIndex + 1) % this.items.length)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      if (this.items.length === 0) return
      this.setActive((this.activeIndex - 1 + this.items.length) % this.items.length)
    } else if (event.key === "Enter") {
      event.preventDefault()
      const item = this.items[this.activeIndex]
      if (item) this.navigateTo(item.path)
    }
  }

  navigateTo(path) {
    window.location.href = path
  }
}
